import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { useEffect, useState } from "react";

/** Account-specific, minimal visual cache. No credentials, profile record or private documents. */
const DB = "mundely-offline-avatar-v1";
const AVATAR_LIMIT = 5_000_000;
type CachedImage = { uid: string; url: string; blob: Blob };

function openAvatarDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains("avatars")) request.result.createObjectStore("avatars", { keyPath: "uid" });
    };
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}
async function cachedAvatar(uid: string, value?: CachedImage): Promise<CachedImage | null> {
  const db = await openAvatarDB();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction("avatars", value ? "readwrite" : "readonly");
      const request = value ? transaction.objectStore("avatars").put(value) : transaction.objectStore("avatars").get(uid);
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve(value || (request.result as CachedImage | undefined) || null);
      transaction.onerror = () => reject(transaction.error);
    });
  } finally { db.close(); }
}
/** Persist a successfully uploaded avatar even if its remote URL is not fetchable. */
export async function cacheProfileAvatarFile(uid: string, blob: Blob): Promise<void> {
  if (!uid || blob.size > AVATAR_LIMIT || !blob.type.startsWith("image/")) return;
  await cachedAvatar(uid, { uid, url: "", blob });
}

async function avatarBytes(url: string): Promise<Blob> {
  if (Capacitor.isNativePlatform() && url.startsWith("https://")) {
    const path = `avatar-${crypto.randomUUID()}`;
    try {
      await Filesystem.downloadFile({ url, path, directory: Directory.Cache });
      if ((await Filesystem.stat({ path, directory: Directory.Cache })).size > AVATAR_LIMIT) throw new Error("avatar/too-large");
      const file = await Filesystem.readFile({ path, directory: Directory.Cache });
      if (typeof file.data !== "string") return file.data;
      const bytes = Uint8Array.from(atob(file.data), char => char.charCodeAt(0));
      if (bytes.length > AVATAR_LIMIT) throw new Error("avatar/too-large");
      return new Blob([bytes.buffer as ArrayBuffer], { type: "image/jpeg" });
    } catch (error) {
      if ((error as Error)?.message === "avatar/too-large") throw error;
      // Some signed URLs are inaccessible through the native downloader; try the WebView fetch.
    } finally {
      await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined);
    }
  }
  const result = await fetch(url, { credentials: "omit", cache: "no-store" });
  if (!result.ok) throw new Error("avatar/not-available");
  const blob = await result.blob();
  if (blob.size > AVATAR_LIMIT) throw new Error("avatar/too-large");
  return blob;
}

export function CachedProfileAvatar({ uid, url, fallback, className = "avatar" }: {
  uid: string; url?: string; fallback: string; className?: string;
}) {
  const [cached, setCached] = useState("");
  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    setCached("");
    if (!uid || typeof indexedDB === "undefined") return;
    const setFromBlob = (blob: Blob) => {
      if (cancelled) return;
      const previous = objectUrl;
      objectUrl = URL.createObjectURL(blob);
      setCached(objectUrl);
      if (previous) URL.revokeObjectURL(previous);
    };
    void (async () => {
      const previous = await cachedAvatar(uid).catch(() => null);
      if (previous?.blob) setFromBlob(previous.blob);
      if (!navigator.onLine || !url) return;
      if (previous?.url === url && previous.blob) return;
      const blob = await avatarBytes(url);
      if (cancelled) return;
      await cachedAvatar(uid, { uid, url, blob });
      setFromBlob(blob);
    })().catch(() => undefined);
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [uid, url]);
  if (cached || (navigator.onLine && url)) return <img className={className} src={cached || url} alt=""/>;
  return <div className={`${className} fallback`}>{fallback.slice(0, 1) || "T"}</div>;
}
